import { loadTemplate } from '@ag-grid-community/core/dist/cjs/utils/dom'
import { prettyFormatNumber } from '@helios/utils'
import { Money, WorkbenchRow } from '../../models/workbench-row.model'
import { isMoney } from '../renderers/renderers'
import {
  statusPanelTemplate,
  WorkbenchStatusBarBase,
} from './workbench-status-bar-base'

/**
 * Base for the money summaries (`Amount:` / `Margin:`). Totals are grouped by currency and each
 * currency becomes an option in a `select` — the same affordance the classic transactions status bar
 * uses so a user can page through currencies instead of reading a run-on string.
 */
abstract class WorkbenchMoneyStatusBarBase extends WorkbenchStatusBarBase {
  protected constructor(
    label: string,
    private readonly containerId: string,
    extraClass: string,
  ) {
    super(
      statusPanelTemplate(
        label,
        `<select class="form-control" id="${containerId}"></select>`,
        extraClass,
      ),
    )
  }

  /** The Money field this panel totals. */
  protected abstract moneyOf(row: WorkbenchRow): Money | undefined

  protected render(rows: WorkbenchRow[]): void {
    const byCurrency = new Map<string, number>()
    rows.forEach(row => {
      const money = this.moneyOf(row)
      if (isMoney(money)) {
        byCurrency.set(
          money.currency,
          (byCurrency.get(money.currency) ?? 0) + money.value,
        )
      }
    })

    // Sources without this field (e.g. margin outside Clearwater) collapse instead of showing a
    // permanently empty label.
    this.setEmptyState(byCurrency.size === 0)

    this.setOptions(
      [...byCurrency.entries()].map(([currency, total]) =>
        loadTemplate(
          `<option>${prettyFormatNumber(total, 4)} ${currency}</option>`,
        ),
      ),
    )
  }

  private setOptions(options: HTMLElement[]): void {
    const select = this.eGui.querySelector<HTMLSelectElement>(
      `#${this.containerId}`,
    )
    if (select == null) return

    // Keep the currency the user was looking at where the new option list still reaches that index.
    const previousIndex = Math.max(0, select.selectedIndex)
    select.innerHTML = ''
    options.forEach(option => select.append(option))
    select.selectedIndex = Math.min(previousIndex, options.length - 1)
  }
}

/** `Amount:` totals for the current selection, grouped by currency. */
export class WorkbenchAmountStatusBarComp extends WorkbenchMoneyStatusBarBase {
  constructor() {
    super('Amount', 'wb-amount-container', 'hs-workbench-amounts-status-bar')
  }

  protected moneyOf(row: WorkbenchRow): Money | undefined {
    return row.amount
  }
}

/** `Margin:` totals for the current selection — Clearwater's margin effect, grouped by currency. */
export class WorkbenchMarginStatusBarComp extends WorkbenchMoneyStatusBarBase {
  constructor() {
    super('Margin', 'wb-margin-container', 'hs-workbench-margin-status-bar')
  }

  protected moneyOf(row: WorkbenchRow): Money | undefined {
    return row.margin
  }
}
